import { z } from 'zod';

/*
 * DijitalApp Assistant (Business): an organisation's own assistant that answers only from the
 * knowledge the organisation uploads (retrieval-augmented), cites its sources and hands over to a
 * person when the answer is not in that knowledge.
 */

export const ASSISTANT_SPACE_TYPES = ['business', 'school', 'community'] as const;
export const ASSISTANT_TONES = ['friendly', 'formal', 'concise'] as const;
export const ASSISTANT_SOURCE_KINDS = ['text', 'faq', 'url'] as const;
export type AssistantTone = (typeof ASSISTANT_TONES)[number];
export type AssistantSourceKind = (typeof ASSISTANT_SOURCE_KINDS)[number];
export type AssistantSourceStatus = 'queued' | 'ready' | 'failed';

/** Per assistant: a large knowledge base still fits comfortably in the hybrid index. */
export const ASSISTANT_LIMITS = { sources: 200, sourceChars: 200_000, questionChars: 1000 } as const;

export const assistantSettingsSchema = z.object({
  name: z.string().trim().min(2).max(60),
  enabled: z.boolean(),
  tone: z.enum(ASSISTANT_TONES),
  instructions: z.string().trim().max(2000),
  welcome: z.string().trim().max(300),
  handoffMessage: z.string().trim().max(300),
  blockedTopics: z.string().trim().max(1000),
});
export type AssistantSettings = z.infer<typeof assistantSettingsSchema>;

export const assistantSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), title: z.string().trim().min(1).max(200), content: z.string().trim().min(1).max(ASSISTANT_LIMITS.sourceChars) }),
  z.object({
    kind: z.literal('faq'),
    title: z.string().trim().min(1).max(200),
    items: z.array(z.object({ q: z.string().trim().min(1).max(500), a: z.string().trim().min(1).max(4000) })).min(1).max(500),
  }),
  z.object({ kind: z.literal('url'), title: z.string().trim().max(200).default(''), url: z.url().max(2000) }),
]);
export type AssistantSourceInput = z.infer<typeof assistantSourceSchema>;

export const assistantAskSchema = z.object({
  question: z.string().trim().min(1).max(ASSISTANT_LIMITS.questionChars),
  conversationId: z.uuid().optional(),
});

export interface AssistantSourceDto {
  id: string;
  kind: AssistantSourceKind;
  title: string;
  url: string | null;
  status: AssistantSourceStatus;
  error: string | null;
  chunks: number;
  embedded: number;
  chars: number;
  updatedAt: string;
}

export interface AssistantDto {
  spaceId: string;
  settings: AssistantSettings;
  sources: AssistantSourceDto[];
  stats: { conversations: number; questions: number; unanswered: number };
  /** What is wired up on the server: answers need `ai`; meaning-based search needs `embeddings`. */
  providers: { ai: boolean; embeddings: boolean; vectorIndex: boolean };
}

export interface AssistantCitation {
  n: number;
  sourceId: string;
  title: string;
  excerpt: string;
}

export interface AssistantAnswerDto {
  conversationId: string;
  /** answered: grounded reply; not_found: nothing in the knowledge base (hand over); ai_unavailable: retrieval only. */
  status: 'answered' | 'not_found' | 'ai_unavailable' | 'refused';
  text: string;
  citations: AssistantCitation[];
}

export interface AssistantUnansweredDto {
  question: string;
  askedAt: string;
  count: number;
}
